import { createI18n } from "vue-i18n";
import store from "@/store";
import moment from "moment-timezone";
import VueCookies from "vue-cookies";

const fallbackLocale = "da";
const availableLanguages = JSON.parse(process.env.VUE_APP_AVAILABLE_LANGUAGES);
const loadedLanguages = [];

let instance;

export const i18nInstance = () => {
    if (!instance) {
        instance = createI18n({
            legacy: false, 
            globalInjection: true, 
            locale: defaultLocale(),
            fallbackLocale,
            messages: {}  
        });
    }
    return instance;
};

function setI18nLanguage(lang) {
    i18nInstance().global.locale.value = lang;
    moment.locale(lang);
    document.querySelector("html").setAttribute("lang", lang);
    store.commit("date/forceDateUpdate");
    return lang;
}

function loadLanguage(lang, langModulePromise) {
    return langModulePromise
        .then(langModule => langModule.default)
        .then(messages => {
            i18nInstance().global.setLocaleMessage(lang, messages);  
            loadedLanguages.push(lang);
            return setI18nLanguage(lang);
        });
}

// Læs lokalisering fra navigator eller cookie
function navigatorLocale() {
    try {
        const locale = (navigator.language || navigator.userLanguage).split("-")[0];
        if (locale) {
            if (availableLanguages.includes(locale)) {
                return locale;
            } else if (availableLanguages.includes("en")) {
                return "en";
            } else {
                return null;
            }
        } else {
            return null;
        }
    } catch (e) {
        return null;
    }
}

function cookieLocale() {
    try {
        const locale = VueCookies.get("locale");  
        return availableLanguages.includes(locale) ? locale : null;
    } catch (e) {
        return null;
    }
}

function saveLocale(locale) {
    try {
        VueCookies.set("locale", locale, Infinity);
    } catch (e) {
        console.error(e);
    }
}

function defaultLocale() {
    const localeFromCookie = cookieLocale();
    const localeFromNavigator = navigatorLocale();
    return localeFromCookie || localeFromNavigator || fallbackLocale;
}

export function loadLanguageAsync(lang, storeInCookie = false) {
    if (!availableLanguages.includes(lang)) {
        return Promise.resolve(i18nInstance().global.locale.value);
    }

    if (storeInCookie) {
        saveLocale(lang);
    }

    if (i18nInstance().global.locale.value === lang) {
        return Promise.resolve(lang);
    }

    if (loadedLanguages.includes(lang)) {
        return Promise.resolve(setI18nLanguage(lang));
    }

    return loadLanguage(
        lang,
        import(
            /* webpackChunkName: "lang-[request]", webpackMode: "lazy" */ `@/i18n/languages/${lang}`  
        )
    );
}

// Init-funktion som din main.js kalder
export default async function i18nInit() {
    const locale = i18nInstance().global.locale.value;

    await loadLanguage(
        locale,
        import(
            /* webpackChunkName: "lang-default", webpackMode: "eager" */ `@/i18n/languages/${locale}`  
        )
    );

    return i18nInstance(); 
}
